import SelectBox from 'devextreme-react/select-box';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import type { UserActivityAnalyticsAccessSection, UserActivityReportMetric } from '../types';
import type { UserActivityAccessSubview } from '../userActivityPrefs';
import { DimensionReportPanel } from './DimensionReportPanel';
import { drilldownForDimension } from './sessionDrilldown';

const SUBVIEW_OPTIONS: { value: UserActivityAccessSubview; text: string }[] = [
  { value: 'lastPath', text: 'Last observed path' },
  { value: 'role', text: 'Role' },
  { value: 'tenant', text: 'Tenant' },
  { value: 'endReason', text: 'End reason' },
  { value: 'status', text: 'Status' }
];

export function AccessReport({
  access,
  chartColors,
  subview,
  onSubviewChange,
  metric,
  onMetricChange,
  showTenantColumn,
  onDrilldown
}: {
  access: UserActivityAnalyticsAccessSection;
  chartColors: ChartThemeColors;
  subview: UserActivityAccessSubview;
  onSubviewChange: (next: UserActivityAccessSubview) => void;
  metric: UserActivityReportMetric;
  onMetricChange: (next: UserActivityReportMetric) => void;
  showTenantColumn: boolean;
  onDrilldown: (label: string, filter: unknown) => void;
}) {
  const effectiveSubview =
    subview === 'tenant' && !showTenantColumn ? 'lastPath' : subview;

  const rows =
    effectiveSubview === 'role'
      ? access.byRole
      : effectiveSubview === 'tenant'
        ? access.byTenant
        : effectiveSubview === 'endReason'
          ? access.byEndReason
          : effectiveSubview === 'status'
            ? access.byStatus
            : access.byLastPath;

  const options = showTenantColumn
    ? SUBVIEW_OPTIONS
    : SUBVIEW_OPTIONS.filter((o) => o.value !== 'tenant');

  return (
    <div className="user-activity-analytics__body user-activity-analytics__body--scroll">
      <div className="user-activity-analytics__chart-header">
        <div>
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Access
          </h3>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--ops-color-muted, #6b7280)', fontSize: 14 }}>
            Last observed path is the most recent path captured on the session — not a page-view
            navigation history.
          </p>
        </div>
        <SelectBox
          dataSource={options}
          value={effectiveSubview}
          valueExpr="value"
          displayExpr="text"
          width={200}
          onValueChanged={(e) => onSubviewChange(e.value as UserActivityAccessSubview)}
        />
      </div>

      <DimensionReportPanel
        title={options.find((o) => o.value === effectiveSubview)?.text ?? 'Access'}
        subtitle="Click View sessions to open the Sessions grid with a matching filter."
        rows={rows}
        colors={chartColors}
        metric={metric}
        onMetricChange={onMetricChange}
        fileNamePrefix={`access-${effectiveSubview}`}
        onRowDrilldown={(row) => {
          const drill = drilldownForDimension(effectiveSubview, row.key, row.label);
          onDrilldown(drill.label, drill.filter);
        }}
      />
    </div>
  );
}
